// src/components/FloorVisualizer.js
import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useContext,
} from "react";
import { AppContext } from "../utilities/context";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material"; // Agregamos Snackbar y Alert para feedback
import DraggableLocation from "./DraggableLocation";
import OrderModalContent from "./OrderModalContent";
import axios from "axios";

function FloorVisualizer({ floorId, autoRefresh = false, timeout = 5000 }) {
  const context = useContext(AppContext);
  const [planta, setPlanta] = useState(null);
  const [locations, setLocations] = useState([]);
  const [plantaLoading, setPlantaLoading] = useState(true);
  const [plantaError, setPlantaError] = useState(null);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [orderToEditId, setOrderToEditId] = useState(null);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, timeout);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData, timeout]);

  const OrderModalContentRef = useRef();
  const handleOpenOrderModalContent = () => {
    setOrderToEditId(null); // Asegurarse de que no haya ID de producto para edición
    OrderModalContentRef.current?.handleClickOpen();
  };

  const handleEditClick = (id) => {
    setOrderToEditId(id); // Establecer el ID del producto a editar
    OrderModalContentRef.current?.handleClickOpen();
    console.log("Edit clicked for row:", id);
  };

  // Solo carga locations, no la planta
  const fetchData = async () => {
    try {
      setLocationsLoading(true);
      setLocationsError(null);
      const locationsRes = await axios.get(
        `/api/locations?floorId=${floorId}&includeOrders=true&minimal=true`
      );
      setLocations(locationsRes.data.locations || []);
    } catch (err) {
      setLocationsError("Error al cargar las ubicaciones.");
      console.error(err);
      setSnackbarMessage("Error al cargar las ubicaciones. Inténtelo de nuevo.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLocationsLoading(false);
    }
  };

  // Solo carga la planta una vez o si cambia floorId
  useEffect(() => {
    if (!floorId) return;
    setPlantaLoading(true);
    setPlantaError(null);
    axios.get(`/api/floors/${floorId}`)
      .then((plantaRes) => {
        setPlanta(plantaRes.data);
      })
      .catch((err) => {
        setPlantaError("Error al cargar los datos del plano.");
        setSnackbarMessage("Error al cargar los datos del plano. Inténtelo de nuevo.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setPlantaLoading(false);
      });
  }, [floorId]);

  useEffect(() => {
    if (floorId) {
      fetchData();
    }
  }, [floorId]);

  const handleMesaDragStop = useCallback(async (locationId, newX, newY) => {
    try {
      // Optimistic update para mejor UX
      setLocations((prevLocations) =>
        prevLocations.map((loc) =>
          loc.id === locationId ? { ...loc, x: newX, y: newY } : loc
        )
      );
      await axios.put(`/api/locations/${locationId}`, { x: newX, y: newY });
      setSnackbarMessage(`Mesa ${locationId} movida exitosamente.`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      console.log(`Mesa ${locationId} movida a X:${newX}, Y:${newY}`);
    } catch (err) {
      console.error("Error al guardar la nueva posición de la mesa:", err);
      setError("No se pudo guardar la nueva posición de la mesa.");
      setSnackbarMessage("Error al guardar la posición de la mesa.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      // Opcional: Revertir el estado si falla la API
      // Para revertir, necesitarías la posición original de la mesa antes del arrastre.
      // Esto podría hacerse guardando la 'prevPosition' en un estado temporal o en el objeto de la mesa.
    }
  }, []);

  // *** NUEVA FUNCIÓN PARA MANEJAR EL CLIC DEL BOTÓN DE DETALLES ***
  const handleViewLocationDetails = useCallback((orderId) => {
    setSnackbarMessage(`Accediendo a detalles de la orden ${orderId}.`);
    setSnackbarSeverity("info");
    setSnackbarOpen(true);
    setOrderToEditId(orderId);
    OrderModalContentRef.current?.handleClickOpen();
  }, []);
  // ***************************************************************

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  if (plantaLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (plantaError) {
    return <Typography color="error">{plantaError}</Typography>;
  }

  if (!planta) {
    return <Typography>No se encontró el plano.</Typography>;
  }

  const backgroundImageStyle = planta.imagePath
    ? { backgroundImage: `url(${planta.imagePath})`, backgroundSize: "cover" }
    : {};

  return (
    <Box
      sx={{
        width: "100%",
        height: "calc(100vh - 64px)",
        overflow: "auto",
        border: "1px solid #ccc",
        p: 1,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: `${planta.width}px`,
          height: `${planta.height}px`,
          backgroundColor: "#f0f0f0",
          ...backgroundImageStyle,
          boxShadow: "0px 0px 5px rgba(0,0,0,0.2)",
        }}
      >
        <Typography
          variant="h5"
          sx={{ p: 2, position: "absolute", top: 0, left: 0 }}
        >
          {planta.name}
        </Typography>

        {locationsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress size={32} />
          </Box>
        ) : locationsError ? (
          <Typography color="error">{locationsError}</Typography>
        ) : (
          Array.isArray(locations) &&
            locations.map((location) => (
              <DraggableLocation
                key={location.id}
                location={location}
                orders={location.orders || []}
                onDragStop={handleMesaDragStop}
                onViewDetails={handleViewLocationDetails}
                isOccupied={location.orders && location.orders.length > 0}
              />
            ))
        )}
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <OrderModalContent
        shopId={context?.shop?.id}
        orderId={orderToEditId}
        onSaveSuccess={fetchData}
        ref={OrderModalContentRef}
      />
    </Box>
  );
}

export default FloorVisualizer;
